/**
 * RequestManager - Prevents duplicate API calls for the same data
 * Useful when multiple components request the same data simultaneously
 */

class RequestManager {
    constructor() {
        this.pendingRequests = new Map();
        this.requestCounts = new Map();
    }

    /**
     * Make a request with deduplication
     * @param {string} key - Unique key for the request
     * @param {Function} requestFn - Function that returns a promise
     * @returns {Promise<any>} - Request result
     */
    async makeRequest(key, requestFn) {
        // If request is already pending, return the existing promise
        if (this.pendingRequests.has(key)) {
            console.log(`Request deduplication: ${key} already in progress`);
            return this.pendingRequests.get(key);
        }

        // Track request count
        this.requestCounts.set(key, (this.requestCounts.get(key) || 0) + 1);

        // Create new request promise
        const promise = requestFn();
        this.pendingRequests.set(key, promise);

        try {
            const result = await promise;
            return result;
        } catch (error) {
            // Re-throw the error so calling code can handle it
            throw error;
        } finally {
            // Clean up the pending request
            this.pendingRequests.delete(key);
        }
    }

    /**
     * Check if a request is currently pending
     * @param {string} key - Request key
     * @returns {boolean} - True if request is pending
     */
    isPending(key) {
        return this.pendingRequests.has(key);
    }

    /**
     * Cancel a pending request
     * @param {string} key - Request key
     */
    cancelRequest(key) {
        const request = this.pendingRequests.get(key);
        if (request && request.cancel) {
            request.cancel();
        }
        this.pendingRequests.delete(key);
    }

    /**
     * Get request statistics
     * @returns {object} - Request stats
     */
    getStats() {
        return {
            pendingRequests: this.pendingRequests.size,
            requestCounts: Object.fromEntries(this.requestCounts),
            totalRequests: Array.from(this.requestCounts.values()).reduce((sum, count) => sum + count, 0),
        };
    }

    /**
     * Clear all pending requests
     */
    clear() {
        this.pendingRequests.clear();
    }

    /**
     * Reset request counts
     */
    resetCounts() {
        this.requestCounts.clear();
    }
}

// Create singleton instance
export const requestManager = new RequestManager();

/**
 * Hook for making deduplicated requests
 * @param {string} requestKey - Unique key for the request
 * @param {Function} requestFn - Function that returns a promise
 * @returns {Promise<any>} - Request result
 */
export const useDeduplicatedRequest = async (requestKey, requestFn) => {
    return requestManager.makeRequest(requestKey, requestFn);
};

/**
 * Utility to create request keys for API calls
 * @param {string} method - HTTP method
 * @param {string} url - API URL
 * @param {object} params - Request parameters
 * @returns {string} - Generated request key
 */
export const createRequestKey = (method, url, params = {}) => {
    const paramString = Object.keys(params)
        .sort()
        .map((key) => `${key}:${JSON.stringify(params[key])}`)
        .join("|");

    return `${method}:${url}${paramString ? `|${paramString}` : ""}`;
};

export default requestManager;
